// src/web/client/src/pages/Messages.jsx
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
// Large toggle for the stacked template cards — a bigger skin over the same
// Radix switch primitive; the toggle LOGIC (handleToggleTemplate → PATCH
// /toggle) is untouched.
import { BigSwitch } from "@/components/ui/big-switch";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { Plus, Settings2, Trash2, AlertTriangle } from "lucide-react";
import { TooltipProvider } from "@/components/ui/tooltip";
import NewTemplateDialog from "../components/messages/NewTemplateDialog";
import TestMessageDialog from "../components/messages/TestMessageDialog";
import { authenticatedFetch } from "@/utils/authenticatedFetch";
// THE single shop resolver: URL ?shop= wins over localStorage, honest null
// when unknown — never a demo fallback. Same source of truth as
// Dashboard/Orders/Layout. Replaces a local helper that silently fell back
// to demo-shop.myshopify.com, showing a real merchant demo data as theirs.
import { getShopDomain } from "@/utils/shopContext";
const API_BASE_URL =
  import.meta.env.VITE_API_URL || "https://api.ecomoria.com/api";

// -------- Helpers --------

// קבלת סטטוס לתצוגת Badge
import { getMessageTypeColor, formatMessageType } from '@/components/activity/messageDisplay';

const CANONICAL_TYPES = [
  { type: "thank_you", label: "Thank you", icon: "🙏" },
  { type: "review_request", label: "Review request", icon: "⭐" },
  { type: "vip_upsell", label: "VIP upsell", icon: "👑" },
  { type: "custom_offer", label: "Custom offer", icon: "🎁" },
];
const TYPE_ICON = Object.fromEntries(CANONICAL_TYPES.map((t) => [t.type, t.icon]));

// -------- הקומפוננטה הראשית --------

// 50 per page is the dashboard sweet spot — enough to give context
// without making the merchant scroll through hundreds of items per
// view. Keeps headers + pagination controls in one viewport on a
// laptop without forcing constant interaction.

// 300ms is the well-trodden debounce for typed search — long enough to
// avoid firing a request on every keystroke (4-5 chars/sec average),
// short enough that the merchant sees results feel "immediate" once
// they stop typing. Shorter (~150ms) wastes round-trips; longer
// (~600ms) starts to feel laggy.

// Templates only (UX brief, Phase B2): the four canonical email types and their
// templates, hosted on Automations. The sent list, its stats and the details
// dialog moved to Activity (components/activity/ActivityLog).
export default function Messages() {
  const { toast } = useToast();
  // Resolved on every render, same as Dashboard — URL ?shop= is authoritative,
  // null means "honestly unknown" and gates rendering + all fetches below.
  const shopDomain = getShopDomain();
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showNewTemplateDialog, setShowNewTemplateDialog] = useState(false);
  const [showTestMessageDialog, setShowTestMessageDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  // Which template's on/off switch is mid-request (disables just that switch).
  const [togglingId, setTogglingId] = useState(null);
  // The type the New Template dialog opens on — set when "Set up" is clicked on
  // a specific type's placeholder card, so the create form starts on that type.
  const [newTemplateType, setNewTemplateType] = useState("thank_you");
  // When set, the dialog is in EDIT mode for this template ("Template settings"
  // on a real card). null = create mode. The same dialog serves both; the save
  // router below picks POST (create) vs PUT (update) accordingly.
  const [editingTemplate, setEditingTemplate] = useState(null);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    // No identified shop → nothing to load; the error card renders instead.
    const shopDomain = getShopDomain();
    if (!shopDomain) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);

    try {
      // First page of messages — uses the same loader as pagination
      // changes, so behaviour is identical and there's only one fetch
      // codepath to maintain.

      // Templates
      try {
        const templatesRes = await authenticatedFetch(
          `${API_BASE_URL}/templates?shop_domain=${shopDomain}`
        );
        if (templatesRes.ok) {
          const templatesData = await templatesRes.json();

          let templatesArray = [];

          if (Array.isArray(templatesData)) {
            templatesArray = templatesData;
          } else if (
            templatesData &&
            Array.isArray(templatesData.templates)
          ) {
            templatesArray = templatesData.templates;
          } else if (templatesData && Array.isArray(templatesData.data)) {
            templatesArray = templatesData.data;
          }

          setTemplates(templatesArray);
        } else {
          setTemplates([]);
        }
      } catch (error) {
        console.error("Error loading templates:", error);
        setTemplates([]);
      }
    } catch (error) {
      console.error("Error loading data:", error);
      toast({
        title: "Error",
        description: "Failed to load messages",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleTemplateCreated = async (templateData) => {
    try {
      const shopDomain = getShopDomain();

      const payload = {
        ...templateData,
        shop_domain: shopDomain,
      };

      const res = await authenticatedFetch(`${API_BASE_URL}/templates`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        throw new Error(`Failed to create template: ${res.status}`);
      }

      await res.json();

      toast({
        title: "Template created",
        description: "Your message template has been saved.",
      });

      await loadData();
      setShowNewTemplateDialog(false);
    } catch (error) {
      console.error("Error creating template:", error);
      toast({
        title: "Error",
        description: "Failed to create template",
        variant: "destructive",
      });
    }
  };

  // Update an existing template via the EXISTING PUT /templates/:id (no new
  // endpoint). The backend does Object.assign(template, req.body), so we send
  // ONLY the fields the settings dialog owns: name, subject, content, trigger.
  // Deliberately NOT is_active (activation belongs to the card's toggle alone —
  // editing must never silently switch automation on/off) and NOT type (the
  // slot identity, locked in the dialog).
  const handleTemplateUpdated = async (templateId, templateData) => {
    try {
      const shopDomain = getShopDomain();
      const body = {
        name: templateData.name,
        subject: templateData.subject,
        content: templateData.content,
        trigger: templateData.trigger,
      };
      const res = await authenticatedFetch(
        `${API_BASE_URL}/templates/${templateId}?shop_domain=${encodeURIComponent(shopDomain)}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json", "x-shop-domain": shopDomain },
          body: JSON.stringify(body),
        }
      );
      if (!res.ok) {
        throw new Error(`Failed to update template: ${res.status}`);
      }
      await res.json();
      toast({
        title: "Template updated",
        description: "Your changes are saved.",
      });
      await loadData();
      setShowNewTemplateDialog(false);
      setEditingTemplate(null);
    } catch (error) {
      console.error("Error updating template:", error);
      toast({
        title: "Error",
        description: "Failed to update template",
        variant: "destructive",
      });
    }
  };

  // One dialog, two flows: EDIT (Template settings on a real card) routes to
  // PUT; CREATE (Set up on a placeholder) keeps the original POST flow.
  const handleDialogSave = async (templateData) => {
    if (editingTemplate) {
      await handleTemplateUpdated(editingTemplate._id, templateData);
    } else {
      await handleTemplateCreated(templateData);
    }
  };

  const handleDeleteTemplate = async (template) => {
    const ok = window.confirm(
      `Delete template "${template.name}"? This action cannot be undone.`
    );
    if (!ok) return;

    try {
      const shopDomain = getShopDomain();
      const res = await authenticatedFetch(
        `${API_BASE_URL}/templates/${template._id}?shop_domain=${encodeURIComponent(shopDomain)}`,
        {
          method: "DELETE",
          headers: { "x-shop-domain": shopDomain },
        }
      );
      if (!res.ok) {
        throw new Error(`Failed to delete template: ${res.status}`);
      }

      toast({
        title: "Template deleted",
        description: "The template has been removed.",
      });

      await loadData();
    } catch (error) {
      console.error("Error deleting template:", error);
      toast({
        title: "Error",
        description: "Failed to delete template",
        variant: "destructive",
      });
    }
  };

  const handleTestMessage = (template) => {
    setSelectedTemplate(template);
    setShowTestMessageDialog(true);
  };

  // Turn a template on/off. Calls the purpose-built PATCH /templates/:id/toggle
  // (which also enforces "one ACTIVE template per type" by deactivating same-type
  // siblings when a template is activated). We touch NO sending logic — activating
  // simply lets the existing automation pick this template up on the next matching
  // order. loadData() reruns afterwards so this card AND any sibling the backend
  // switched off both reflect the new truth.
  const handleToggleTemplate = async (template) => {
    const activating = !template.is_active;
    // Surface the enforced "one active per type" rule honestly: if another
    // template of this type is currently active, activating THIS one replaces it.
    const replaced = activating
      ? templates.find(
          (t) => t.type === template.type && t.is_active && t._id !== template._id
        )
      : null;

    setTogglingId(template._id);
    try {
      const shopDomain = getShopDomain();
      const res = await authenticatedFetch(
        `${API_BASE_URL}/templates/${template._id}/toggle?shop_domain=${encodeURIComponent(shopDomain)}`,
        { method: "PATCH", headers: { "x-shop-domain": shopDomain } }
      );
      if (!res.ok) throw new Error(`Failed to toggle template: ${res.status}`);

      const typeLabel = formatMessageType(template.type);
      if (activating) {
        toast({
          title: "Template activated",
          description:
            `Ecomoria will now send ${typeLabel} emails automatically on matching orders.` +
            (replaced
              ? ` This replaced “${replaced.name}” as your active ${typeLabel} email.`
              : ""),
        });
      } else {
        toast({
          title: "Template paused",
          description: `No new ${typeLabel} emails will be sent from this template.`,
        });
      }

      await loadData();
    } catch (error) {
      console.error("Error toggling template:", error);
      toast({
        title: "Couldn't update template",
        description: "Please try again.",
        variant: "destructive",
      });
    } finally {
      setTogglingId(null);
    }
  };

  // A real (backed-by-a-template) type card, stacked full-width. New SKIN only:
  // the big toggle calls the SAME handleToggleTemplate → PATCH /toggle (#108),
  // with the same replacement toast; activation state must read instantly —
  // active = full color + emerald frame, inactive = greyed out. `count` > 1
  // keeps the honest multi-template hint. Test + delete stay as quiet
  // secondary actions (capability preserved; the content itself now lives
  // behind Template settings).
  const renderTemplateCard = (template, count) => {
    const active = !!template.is_active;
    return (
      <div
        key={template.type}
        className={`w-full rounded-xl border p-5 transition-colors ${
          active
            ? "bg-white border-2 border-emerald-200 shadow-sm"
            : "bg-gray-50 border-gray-200 opacity-70"
        }`}
      >
        {/* Row 1 — identity (icon, name, TYPE chip, status chip, subject) +
            the big switch. Two-row layout: chips can wrap freely here and can
            never collide with the action buttons, which live on their own row. */}
        <div className="flex items-start gap-4">
          <span
            className={`w-11 h-11 rounded-full grid place-items-center text-lg shrink-0 ${
              active ? getMessageTypeColor(template.type) : "bg-gray-200 text-gray-400 grayscale"
            }`}
          >
            {TYPE_ICON[template.type] || "✉️"}
          </span>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className={`font-semibold truncate ${active ? "text-gray-900" : "text-gray-500"}`}>
                {template.name}
              </h3>
              {/* The FIXED type chip — the merchant's free-form name ("chana",
                  "t") must never be the only way to tell what this email IS. */}
              <Badge className={`${getMessageTypeColor(template.type)} whitespace-nowrap`}>
                {formatMessageType(template.type)}
              </Badge>
              {active ? (
                <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full whitespace-nowrap">
                  Active · Sending automatically
                </span>
              ) : (
                <span className="text-[11px] font-medium text-gray-400 bg-white border px-2 py-0.5 rounded-full whitespace-nowrap">
                  Off · Automation off
                </span>
              )}
              {count > 1 && (
                <span className="text-[11px] text-amber-600 whitespace-nowrap">
                  {count} templates · managing the active one
                </span>
              )}
            </div>
            <p className={`text-sm truncate mt-0.5 ${active ? "text-gray-500" : "text-gray-400"}`}>
              {template.subject}
            </p>
          </div>

          <BigSwitch
            checked={active}
            disabled={togglingId === template._id}
            onCheckedChange={() => handleToggleTemplate(template)}
            aria-label={`${active ? "Deactivate" : "Activate"} ${template.name}`}
            className="mt-1"
          />
        </div>

        {/* Row 2 — actions, on their own line so nothing overlaps at any width. */}
        <div className="flex items-center justify-end gap-2 mt-4 pt-3 border-t border-gray-100">
          <Button
            size="sm"
            variant="outline"
            className="shrink-0"
            onClick={() => {
              setEditingTemplate(template);
              setShowNewTemplateDialog(true);
            }}
          >
            <Settings2 className="w-3.5 h-3.5 mr-1.5" />
            Template settings
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="shrink-0 text-gray-500"
            onClick={() => handleTestMessage(template)}
          >
            Test
          </Button>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="h-8 w-8 shrink-0 text-slate-400 hover:text-red-500 hover:bg-red-50"
            onClick={() => handleDeleteTemplate(template)}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  };

  // A placeholder card for a type with NO template yet, stacked full-width.
  // The big switch is OFF and DISABLED — it never calls toggle on a
  // non-existent id; the only way to turn this type on is to create it via
  // "Set up" (opens the create dialog on this type). Off = no automation.
  const renderPlaceholderCard = (type, label) => (
    <div
      key={type}
      className="w-full rounded-xl border border-dashed border-gray-300 bg-gray-50/70 p-5 flex items-center gap-4 opacity-80"
    >
      <span className="w-11 h-11 rounded-full bg-gray-200 text-gray-400 grid place-items-center text-lg shrink-0 grayscale">
        {TYPE_ICON[type] || "✉️"}
      </span>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-semibold text-gray-500 truncate">{label}</h3>
          {/* Muted fixed type chip — same convention as the real cards. */}
          <Badge className={`${getMessageTypeColor(type)} whitespace-nowrap opacity-70`}>
            {formatMessageType(type)}
          </Badge>
          <span className="text-[11px] font-medium text-gray-400 bg-white border px-2 py-0.5 rounded-full whitespace-nowrap">
            Not set up yet
          </span>
        </div>
        <p className="text-sm text-gray-400 mt-0.5">Set up to turn on</p>
      </div>

      <Button
        size="sm"
        className="shrink-0 bg-indigo-600 hover:bg-indigo-700 text-white"
        onClick={() => {
          setEditingTemplate(null);
          setNewTemplateType(type);
          setShowNewTemplateDialog(true);
        }}
      >
        Set up
      </Button>

      <BigSwitch checked={false} disabled aria-label={`${label} — not set up`} />
    </div>
  );


  // Honest failure state (same pattern as Dashboard): we don't know which
  // store this is. Never fall back to demo data here — that would show a
  // merchant messages and templates that are not theirs.
  if (!shopDomain) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <Card className="max-w-md w-full">
          <CardContent className="flex flex-col items-center text-center py-10 space-y-4">
            <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-7 h-7 text-amber-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900">We couldn't identify your store</h3>
            <p className="text-sm text-gray-500 leading-relaxed">
              Please open Ecomoria from your Shopify admin (Apps → Ecomoria) so we
              can load your store's data.
            </p>
            <Button onClick={() => window.location.reload()} variant="outline">
              Reload
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading messages...</p>
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div>
        <div className="max-w-7xl mx-auto space-y-6">

          {/* Templates */}
          <Card>
            <CardHeader>
              <CardTitle>Message Templates</CardTitle>
            </CardHeader>
            <CardContent>
              {/* The 4 canonical types, always shown as a fixed menu — a new
                  merchant sees the full set (all OFF) instead of an empty card.
                  A type with a template binds to its ACTIVE one (or the first if
                  none active); a type with none shows a placeholder whose switch
                  is disabled — you turn a type on by setting it up, never by
                  toggling something that doesn't exist. */}
              <div className="space-y-4">
                {CANONICAL_TYPES.map(({ type, label }) => {
                  const ofType = templates.filter((t) => t.type === type);
                  const primary = ofType.find((t) => t.is_active) || ofType[0] || null;
                  return primary
                    ? renderTemplateCard(primary, ofType.length)
                    : renderPlaceholderCard(type, label);
                })}
              </div>
            </CardContent>
          </Card>
          {/* Dialogs */}
          <NewTemplateDialog
            open={showNewTemplateDialog}
            initialType={newTemplateType}
            template={editingTemplate}
            onClose={() => {
              setShowNewTemplateDialog(false);
              setEditingTemplate(null);
            }}
            onSave={handleDialogSave}
          />

          {selectedTemplate && (
            <TestMessageDialog
              open={showTestMessageDialog}
              onClose={() => setShowTestMessageDialog(false)}
              template={selectedTemplate}
              onRefresh={loadData}
            />
          )}

        </div>
      </div>
    </TooltipProvider>
  );
}
